/**
 * `/parent/attendance` — the Parent Portal attendance view (P-SCH-V1, ADR-0103).
 *
 * The selected child's attendance log in a flat, read-only table (date · course
 * · status · note), from `learning.learner.guardian.childAttendance`. Status is
 * a StatusPill (present / late / excused / absent — vocabulary inlined). Read
 * only — a guardian never edits a child's attendance.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { DataTable, type DataTableColumn } from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { ParentRoute } from '../components/parent-portal/parent-shell.js';
import { useSelectedChild, type ParentChild } from '../components/parent-portal/child-selector.js';
import {
  AttendanceStatusPill,
  ChildScaffold,
  InlineError,
  formatDate,
} from '../components/parent-portal/parent-shared.js';

export const Route = createFileRoute('/parent/attendance')({
  component: ParentAttendancePage,
});

type AttendanceRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childAttendance']['rows'][number];

function ParentAttendancePage(): React.JSX.Element {
  return (
    <ParentRoute title="Attendance">
      <ParentAttendanceContent />
    </ParentRoute>
  );
}

function ParentAttendanceContent(): React.JSX.Element {
  const { query, children, selected, setSelected } = useSelectedChild();
  return (
    <ChildScaffold
      heroTitle="Attendance"
      heroSubtitle="Your student’s session attendance across their courses."
      query={query}
      children={children}
      selected={selected}
      setSelected={setSelected}
      renderBody={(child) => <AttendanceTable child={child} />}
    />
  );
}

function AttendanceTable({ child }: { child: ParentChild }): React.JSX.Element {
  const attendanceQ = trpc.learning.learner.guardian.childAttendance.useQuery({
    studentUserId: child.studentUserId,
  });
  const rows = attendanceQ.data?.rows ?? [];

  const columns: DataTableColumn<AttendanceRow & { _i: number }>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (r) => <span className="font-medium text-foreground">{formatDate(r.date)}</span>,
    },
    {
      key: 'course',
      header: 'Course',
      cell: (r) => <span className="text-muted-foreground">{r.courseTitle ?? '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <AttendanceStatusPill status={r.status} />,
    },
    {
      key: 'note',
      header: 'Note',
      cell: (r) =>
        r.note ? (
          <span className="text-muted-foreground">{r.note}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
    },
  ];

  if (attendanceQ.isError) return <InlineError onRetry={() => void attendanceQ.refetch()} />;

  return (
    <DataTable
      columns={columns}
      rows={rows.map((r, i) => ({ ...r, _i: i }))}
      rowKey={(r) => `${r.date}-${r._i}`}
      isLoading={attendanceQ.isPending}
      emptyTitle="No attendance recorded yet"
      emptyDescription="Session attendance will appear here once your student’s classes begin."
      showIndex
    />
  );
}
